import { Link, Text, View } from '@react-pdf/renderer';
import type { ListItem, PhrasingContent, RootContent } from 'mdast';
import type { FC, ReactNode } from 'react';
import { markdownNodeToPlainText, parseMarkdownParts } from '@/utils/markdown';
import styles from './pdfStyles';

interface PdfMdTextProps {
  content: string;
}

function renderUnsupportedBlock(title: string, body: string, key: string) {
  return (
    <View key={key} style={styles.mdUnsupportedBlock}>
      <Text style={styles.mdUnsupportedTitle}>{title}</Text>
      <Text style={styles.mdUnsupportedText}>{body}</Text>
    </View>
  );
}

function renderInline(nodes: PhrasingContent[], keyPrefix: string): ReactNode[] {
  return nodes.map((node, index) => {
    const key = `${keyPrefix}-${index}`;

    switch (node.type) {
      case 'text':
        return <Text key={key}>{node.value}</Text>;
      case 'strong':
        return (
          <Text key={key} style={styles.mdBold}>
            {renderInline(node.children, `${key}-strong`)}
          </Text>
        );
      case 'emphasis':
        return (
          <Text key={key} style={styles.mdItalic}>
            {renderInline(node.children, `${key}-em`)}
          </Text>
        );
      case 'link':
        return (
          <Link key={key} src={node.url} style={styles.mdLink}>
            {renderInline(node.children, `${key}-link`)}
          </Link>
        );
      case 'inlineCode':
        return (
          <Text key={key} style={styles.mdInlineCode}>
            {node.value}
          </Text>
        );
      case 'break':
        return <Text key={key}>{'\n'}</Text>;
      default: {
        const fallbackText = markdownNodeToPlainText(node);
        return fallbackText ? <Text key={key}>{fallbackText}</Text> : null;
      }
    }
  });
}

function renderListItem(
  item: ListItem,
  index: number,
  ordered: boolean,
  start: number,
  depth: number,
  keyPrefix: string,
): ReactNode {
  const key = `${keyPrefix}-item-${index}`;
  const bullet = ordered ? `${start + index}.` : '•';
  const firstChild = item.children[0];
  const paragraphChildren = firstChild?.type === 'paragraph' ? firstChild.children : null;
  const nestedChildren = paragraphChildren ? item.children.slice(1) : item.children;

  return (
    <View key={key} style={depth > 0 ? { marginLeft: 12 } : undefined}>
      <View style={styles.mdListItem}>
        <Text style={styles.mdBullet}>{bullet}</Text>
        <Text style={styles.mdListText}>
          {paragraphChildren
            ? renderInline(paragraphChildren, `${key}-inline`)
            : markdownNodeToPlainText(item)}
        </Text>
      </View>
      {nestedChildren.length > 0 ? (
        <View style={styles.mdNestedList}>
          {nestedChildren.map((child, childIndex) =>
            renderBlock(child, `${key}-nested-${childIndex}`, depth + 1),
          )}
        </View>
      ) : null}
    </View>
  );
}

function renderBlock(node: RootContent, key: string, depth = 0): ReactNode {
  switch (node.type) {
    case 'heading': {
      const headingStyle =
        node.depth === 1 ? styles.mdH1 : node.depth === 2 ? styles.mdH2 : styles.mdH3;
      return (
        <Text key={key} style={headingStyle}>
          {renderInline(node.children, `${key}-heading`)}
        </Text>
      );
    }
    case 'paragraph':
      return (
        <Text key={key} style={styles.mdParagraph}>
          {renderInline(node.children, `${key}-paragraph`)}
        </Text>
      );
    case 'blockquote':
      return (
        <View key={key} style={styles.mdBlockquote}>
          {node.children.map((child, index) => renderBlock(child, `${key}-quote-${index}`, depth))}
        </View>
      );
    case 'list': {
      const start = node.start ?? 1;
      const ordered = Boolean(node.ordered);
      return (
        <View key={key}>
          {node.children.map((item, index) =>
            renderListItem(item, index, ordered, start, depth, key),
          )}
        </View>
      );
    }
    case 'thematicBreak':
      return <View key={key} style={styles.mdHr} />;
    case 'code':
      return (
        <View key={key} style={styles.mdCodeBlock}>
          <Text style={styles.mdCodeText}>{node.value}</Text>
        </View>
      );
    case 'table':
      return renderUnsupportedBlock(
        'Table omitted from PDF export',
        'This markdown table is supported in the web view but not yet rendered in PDF.',
        key,
      );
    case 'math':
      return renderUnsupportedBlock(
        'Math omitted from PDF export',
        'This formula is supported in the web view but not yet rendered in PDF.',
        key,
      );
    case 'html':
      return renderUnsupportedBlock(
        'HTML omitted from PDF export',
        'Embedded HTML is not rendered in PDF exports.',
        key,
      );
    case 'definition':
      return null;
    default: {
      const fallbackText = markdownNodeToPlainText(node);
      if (!fallbackText) {
        return null;
      }

      return (
        <Text key={key} style={styles.mdParagraph}>
          {fallbackText}
        </Text>
      );
    }
  }
}

export const PdfMdText: FC<PdfMdTextProps> = ({ content }) => {
  const parts = parseMarkdownParts(content);

  return (
    <View>
      {parts.map((part, partIndex) => {
        if (part.type === 'mermaid') {
          return renderUnsupportedBlock(
            'Mermaid diagram omitted from PDF export',
            'This diagram remains available in the web view but is not yet rendered in PDF.',
            `mermaid-${partIndex}`,
          );
        }

        return (
          <View key={`markdown-${partIndex}`}>
            {part.tree.children.map((node, nodeIndex) =>
              renderBlock(node, `markdown-${partIndex}-${nodeIndex}`),
            )}
          </View>
        );
      })}
    </View>
  );
};
